<template>
  <a-modal
    v-model:visible="state.configModalVisible"
    :title="isEdit ? '编辑扶持配置' : '新建扶持配置'"
    :width="600"
    :footer="null"
    @cancel="state.closeConfigModal()"
  >
    <div class="config-modal">
      <!-- 上下文配置 -->
      <div class="config-modal__section">
        <div class="config-modal__title">上下文配置</div>
        <div class="config-modal__context">
          <span>当前上下文：</span>
          <a-tag color="blue">{{ currentBrandName }}</a-tag>
          <span>/</span>
          <a-tag color="blue">{{ currentChannelName }}</a-tag>
          <span>/</span>
          <a-tag color="blue">{{ currentSiteName }}</a-tag>
        </div>
        <div class="config-modal__hint">（继承自当前列表，不可修改）</div>
      </div>

      <a-divider />

      <!-- 品类配置 -->
      <div class="config-modal__section">
        <div class="config-modal__title">品类配置</div>
        <div class="config-modal__row">
          <div class="config-modal__field">
            <label>一级品类 <span class="required">*</span></label>
            <a-input
              v-if="isEdit"
              :value="editCategoryParts[0] || ''"
              disabled
              style="width: 100%"
            />
            <a-select
              v-else
              v-model:value="state.configForm.firstCategoryId"
              style="width: 100%"
              placeholder="请选择"
              allow-clear
              @change="state.onConfigCategory1Change()"
            >
              <a-select-option
                v-for="item in state.category1List"
                :key="item.id"
                :value="item.id"
              >
                {{ item.name }}
              </a-select-option>
            </a-select>
          </div>
          <div class="config-modal__field">
            <label>二级品类 <span class="optional">（可选）</span></label>
            <a-input
              v-if="isEdit"
              :value="editCategoryParts[1] || ''"
              disabled
              style="width: 100%"
            />
            <a-select
              v-else
              v-model:value="state.configForm.secondCategoryId"
              style="width: 100%"
              placeholder="请选择"
              allow-clear
              :disabled="!state.configForm.firstCategoryId"
              @change="state.onConfigCategory2Change()"
            >
              <a-select-option
                v-for="item in state.configFormCategory2List"
                :key="item.id"
                :value="item.id"
              >
                {{ item.name }}
              </a-select-option>
            </a-select>
          </div>
          <div class="config-modal__field">
            <label>三级品类 <span class="optional">（可选）</span></label>
            <a-input
              v-if="isEdit"
              :value="editCategoryParts[2] || ''"
              disabled
              style="width: 100%"
            />
            <a-select
              v-else
              v-model:value="state.configForm.thirdCategoryId"
              style="width: 100%"
              placeholder="请选择"
              allow-clear
              :disabled="!state.configForm.secondCategoryId"
            >
              <a-select-option
                v-for="item in state.configFormCategory3List"
                :key="item.id"
                :value="item.id"
              >
                {{ item.name }}
              </a-select-option>
            </a-select>
          </div>
        </div>
        <div v-if="isEdit" class="config-modal__hint">
          （继承自当前配置，不可修改）
        </div>
      </div>

      <a-divider />

      <!-- 扶持周期与UV目标 -->
      <div
        class="config-modal__section"
        :class="{ 'config-modal__section--disabled': !categorySelected }"
      >
        <div class="config-modal__title">
          扶持周期与UV目标
          <span v-if="!categorySelected" class="config-modal__lock-hint">
            （请先选择品类）
          </span>
        </div>
        <div class="config-modal__row">
          <div class="config-modal__field">
            <label>年份 <span class="required">*</span></label>
            <a-select
              v-model:value="state.configForm.selectedYear"
              style="width: 100%"
              placeholder="请选择"
              :disabled="!categorySelected"
              @change="state.onYearChange()"
            >
              <a-select-option
                v-for="year in state.yearOptions"
                :key="year"
                :value="year"
              >
                {{ year }}
              </a-select-option>
            </a-select>
          </div>
          <div class="config-modal__field">
            <label>起始CW <span class="required">*</span></label>
            <a-select
              v-model:value="state.configForm.startCW"
              style="width: 100%"
              placeholder="请选择"
              :disabled="!cwEnabled"
              @change="state.onStartCWChange()"
            >
              <a-select-option
                v-for="cw in state.cwOptions"
                :key="cw"
                :value="cw"
              >
                {{ cw }}
              </a-select-option>
            </a-select>
          </div>
          <div class="config-modal__field">
            <label>结束CW <span class="required">*</span></label>
            <a-select
              v-model:value="state.configForm.endCW"
              style="width: 100%"
              placeholder="请选择"
              :disabled="!cwEnabled"
              @change="state.onEndCWChange()"
            >
              <a-select-option v-for="cw in endCWOptions" :key="cw" :value="cw">
                {{ cw }}
              </a-select-option>
            </a-select>
          </div>
          <div class="config-modal__field">
            <label>UV基准值 <span class="required">*</span></label>
            <a-input-number
              v-model:value="state.configForm.uvTarget"
              style="width: 100%"
              :min="1"
              :max="9999999"
              placeholder="请输入"
              :disabled="!cwEnabled"
            />
          </div>
        </div>
      </div>

      <a-divider />

      <!-- 逐周精细调整（可选） -->
      <div
        class="config-modal__section"
        :class="{ 'config-modal__section--disabled': !categorySelected }"
      >
        <div
          class="config-modal__collapse-header"
          @click="categorySelected && state.toggleWeeklyAdjust()"
        >
          <span>逐周精细调整（可选）</span>
          <a-tooltip
            title="留空=沿用基准值；填0=该周跳过扶持；填其他数值=覆盖该周"
          >
            <info-circle-outlined class="config-modal__info-icon" />
          </a-tooltip>
          <span class="config-modal__collapse-arrow">
            <up-outlined v-if="state.weeklyAdjustExpanded" />
            <down-outlined v-else />
          </span>
        </div>
        <div v-if="state.weeklyAdjustExpanded" class="config-modal__weekly">
          <div
            v-if="state.configFormCWRange.length === 0"
            class="config-modal__weekly-empty"
          >
            请先选择起始CW和结束CW
          </div>
          <template v-else>
            <div class="config-modal__weekly-header">
              <span>周次</span>
              <span>UV 目标值</span>
            </div>
            <div
              v-for="cw in state.configFormCWRange"
              :key="cw"
              class="config-modal__weekly-row"
            >
              <span class="config-modal__weekly-label">{{ cw }}</span>
              <a-input-number
                :value="state.configForm.weeklyOverrides[cw]"
                style="width: 200px"
                :min="0"
                :max="9999999"
                :step="1"
                placeholder="沿用基准值"
                @change="val => (state.configForm.weeklyOverrides[cw] = val)"
              />
            </div>
            <div class="config-modal__weekly-hint">
              留空的周次将使用上方基准值{{
                state.configForm.uvTarget
                  ? `（${state.configForm.uvTarget.toLocaleString()} UV）`
                  : ''
              }}
            </div>
          </template>
        </div>
      </div>

      <!-- 底部按钮 -->
      <div class="config-modal__footer">
        <a-button @click="state.closeConfigModal()">取消</a-button>
        <template v-if="isEdit">
          <a-button type="primary" @click="state.updateConfig()">
            保存修改
          </a-button>
        </template>
        <template v-else>
          <a-button @click="state.saveConfig(0)">保存</a-button>
          <a-button type="primary" @click="state.saveConfig(1)">
            保存并启用
          </a-button>
        </template>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { inject, computed } from 'vue'
import {
  InfoCircleOutlined,
  UpOutlined,
  DownOutlined
} from '@ant-design/icons-vue'

export default {
  name: 'ConfigDrawer',
  components: {
    InfoCircleOutlined,
    UpOutlined,
    DownOutlined
  },
  setup() {
    const state = inject('initState')

    const currentYear = new Date().getFullYear()

    const isEdit = computed(() => state.configModalMode === 'edit')

    const editCategoryParts = computed(() => state.editCategoryParts || [])

    const currentBrandName = computed(() => {
      const brand = (state.brands || []).find(
        item => item.id === state.searchParams.brandId
      )
      return brand?.name || ''
    })

    const currentChannelName = computed(() => {
      const channel = (state.channels || []).find(
        item => item.id === state.searchParams.channelId
      )
      return channel?.name || ''
    })

    const currentSiteName = computed(() => {
      const site = (state.sites || []).find(
        item => item.id === state.searchParams.siteId
      )
      return site?.name || ''
    })

    const endCWOptions = computed(() => {
      if (!state.configForm.startCW) return state.cwOptions
      const startIdx = state.cwOptions.indexOf(state.configForm.startCW)
      return state.cwOptions.slice(startIdx)
    })

    const categorySelected = computed(() => {
      return isEdit.value ? true : !!state.configForm.firstCategoryId
    })

    const cwEnabled = computed(() => {
      return isEdit.value
        ? true
        : !!state.configForm.firstCategoryId && !!state.configForm.selectedYear
    })

    return {
      state,
      currentYear,
      isEdit,
      editCategoryParts,
      currentBrandName,
      currentChannelName,
      currentSiteName,
      endCWOptions,
      categorySelected,
      cwEnabled
    }
  }
}
</script>

<style lang="scss" scoped>
.config-modal {
  &__section {
    margin-bottom: 4px;

    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__lock-hint {
    font-size: 12px;
    color: #999;
    font-weight: normal;
  }

  &__title {
    font-weight: 500;
    font-size: 14px;
    margin-bottom: 12px;
  }

  &__context {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__hint {
    color: #999;
    font-size: 12px;
    margin-top: 8px;
  }

  &__row {
    display: flex;
    gap: 12px;
  }

  &__field {
    flex: 1;

    label {
      display: block;
      margin-bottom: 6px;
      font-size: 13px;
    }
  }

  &__collapse-header {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
    font-weight: 500;
    font-size: 14px;
  }

  &__info-icon {
    color: #999;
    font-size: 14px;
  }

  &__collapse-arrow {
    margin-left: auto;
    color: #999;
  }

  &__weekly {
    margin-top: 12px;
    padding: 16px;
    background: #fafafa;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
  }

  &__weekly-empty {
    color: #999;
    text-align: center;
    padding: 12px 0;
  }

  &__weekly-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 8px;
    margin-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
    font-weight: 500;
    font-size: 13px;
    color: #666;

    span:first-child {
      width: 80px;
    }
  }

  &__weekly-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 0;
  }

  &__weekly-label {
    width: 80px;
    font-size: 13px;
  }

  &__weekly-hint {
    margin-top: 12px;
    color: #999;
    font-size: 12px;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
  }
}

.required {
  color: #ff4d4f;
}

.optional {
  color: #999;
  font-size: 12px;
}
</style>
